import dayjs, { type Dayjs } from "dayjs";
import type { SchedulePick, ScheduleSlot } from "../../service/schedulePick.service";

/** Most patients the card lists when several are booked around now; the rest are in See all. */
export const MAX_CANDIDATES = 3;

/**
 * What the Start card offers. The schedule pick is right first time only about 41% of
 * the time (64% in the top three; see ``client.schedule_pick``), mostly because several
 * patients are booked around the same time, so a single "Quick Start" is the default
 * only when there is one answer:
 *
 * - ``quick``: one undocumented patient booked around now, or one the clinician chose in
 *   See all. The primary button is Quick Start with their first name on it.
 * - ``choose``: several booked around now. Each is a one-click row; the clinician says who
 *   is in the room instead of the card guessing.
 * - ``later``: nobody booked around now (before the day starts, a gap, running far behind).
 *   The next appointment is a guess, so Start Encounter is the primary and Quick Start for
 *   that appointment is offered beside it.
 * - ``dismissed``: the clinician turned this appointment's Quick Start down (×). Start
 *   Encounter is the primary until the schedule moves on to someone else.
 * - ``none``: no schedule today, not synced, or everyone is documented.
 */
export type QuickStartView =
  | { kind: "quick"; slot: ScheduleSlot; chosen: boolean }
  | { kind: "choose"; slots: ScheduleSlot[]; more: number }
  | { kind: "later"; slot: ScheduleSlot }
  | { kind: "dismissed"; slot: ScheduleSlot }
  | { kind: "none" };

/** Today's undocumented appointments in the server's order: the Recommended group, then the rest of the day. */
export const upcomingSlots = (pick: SchedulePick | null | undefined): ScheduleSlot[] => {
  if (!pick || pick.state !== "ready") return [];
  return [...pick.recommended, ...pick.up_next].filter((slot) => slot.client.id != null);
};

export const quickStartView = (
  pick: SchedulePick | null | undefined,
  { chosenId, dismissedId }: { chosenId?: number | null; dismissedId?: number | null } = {},
): QuickStartView => {
  const upcoming = upcomingSlots(pick);
  if (!pick || upcoming.length === 0) return { kind: "none" };

  // A patient picked from See all stays on the card until a note for them starts.
  const chosen = chosenId != null ? upcoming.find((slot) => slot.client.id === chosenId) : undefined;
  if (chosen) return { kind: "quick", slot: chosen, chosen: true };

  const head = upcoming[0];
  if (dismissedId != null && head.client.id === dismissedId) return { kind: "dismissed", slot: head };
  if (pick.suggestion?.confidence !== "high") return { kind: "later", slot: head };

  // With a confident pick the server's Recommended group is exactly the undocumented
  // patients booked around now (an hour back to 15 minutes ahead), pick first.
  const aroundNow = pick.recommended.filter((slot) => slot.client.id != null);
  if (aroundNow.length <= 1) return { kind: "quick", slot: head, chosen: false };
  return {
    kind: "choose",
    slots: aroundNow.slice(0, MAX_CANDIDATES),
    more: Math.max(aroundNow.length - MAX_CANDIDATES, 0),
  };
};

export const firstNameOf = (name: string | null | undefined): string => (name || "").trim().split(/\s+/)[0] || "";

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;

/** The chip on the card: "in 15 mins", "Now", "20 mins ago", "in 2 hrs 5 mins", "14 hrs ago" (minutes dropped from 3 hrs). */
export const slotTimingLabel = (scheduledAt: string, now: Dayjs = dayjs()): string => {
  const at = dayjs(scheduledAt);
  if (!at.isValid()) return "";
  const minutes = Math.round(at.diff(now, "second") / 60);
  if (Math.abs(minutes) <= 2) return "Now";
  const span = Math.abs(minutes);
  const hours = Math.floor(span / 60);
  const rest = span % 60;
  const text =
    hours === 0
      ? plural(span, "min")
      : rest === 0 || hours >= 3
        ? plural(hours, "hr")
        : `${plural(hours, "hr")} ${plural(rest, "min")}`;
  return minutes > 0 ? `in ${text}` : `${text} ago`;
};

const DISMISSED_KEY = "quick_start_dismissed";

/** The appointment whose Quick Start was turned down (×), remembered for that clinic day. */
export const loadDismissed = (day: string | null | undefined): number | null => {
  if (!day) return null;
  try {
    const stored = JSON.parse(localStorage.getItem(DISMISSED_KEY) || "null") as { day?: string; id?: unknown } | null;
    return stored && stored.day === day && typeof stored.id === "number" ? stored.id : null;
  } catch {
    return null;
  }
};

export const saveDismissed = (day: string, id: number | null): void => {
  if (id == null) {
    localStorage.removeItem(DISMISSED_KEY);
    return;
  }
  localStorage.setItem(DISMISSED_KEY, JSON.stringify({ day, id }));
};
